import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe, TitleService } from '@delon/theme';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { finalize, forkJoin, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { StartupService } from 'src/app/core/startup/startup.service';
import { HttpService } from 'src/app/http/http.service';
import { ImageService } from 'src/app/services/image.service';
import { TenantSwitchService } from 'src/app/services/tenant-switch.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

import { GlobalGuardService } from '../../../providers/global-guard.service';

@Component({
  selector: 'header-tenant',
  template: `
    <div
      class="alain-default__nav-item d-flex align-items-center px-sm tenant-selector"
      nz-dropdown
      [nzDropdownMenu]="tenantMenu"
      nzTrigger="click"
      nzPlacement="bottomRight"
      (click)="onTenantSelectorClick($event)"
      [testId]="'tenant-selector'"
    >
      <nz-avatar [nzSrc]="getTenantAvatar(currentTenant)" nzSize="small" class="mr-sm tenant-avatar" [testId]="'current-tenant-avatar'" />
      <span class="hidden-mobile tenant-name" [testId]="'current-tenant-name'">
        {{ currentTenant?.shortName ?? 'tenant.select-tenant' | i18n }}
      </span>
      <i nz-icon [nzType]="isLoading ? 'loading' : 'down'" class="tenant-arrow ml-xs" [testId]="'tenant-dropdown-arrow'"></i>
    </div>
    <nz-dropdown-menu #tenantMenu="nzDropdownMenu">
      <div nz-menu class="tenant-menu" [testId]="'tenant-dropdown-menu'">
        <div nz-menu-item class="tenant-header" [testId]="'tenant-menu-header'">
          <strong>{{ 'tenant.switch-tenant' | i18n }}</strong>
        </div>
        <div
          nz-menu-item
          *ngFor="let tenant of tenantList"
          (click)="switchTenant(tenant)"
          class="tenant-item"
          [class.tenant-item-selected]="tenant.id === currentTenant?.id"
          [testId]="'tenant-item-' + tenant.id"
        >
          <div class="d-flex align-items-center" [testId]="'tenant-item-content-' + tenant.id">
            <nz-avatar [nzSrc]="getTenantAvatar(tenant)" [nzSize]="32" class="mr-sm tenant-avatar" [testId]="'tenant-item-avatar'" />
            <div class="tenant-item-text">
              <div class="tenant-line1">
                <span class="tenant-item-name" [testId]="'tenant-item-name'">{{ formatName(tenant.name || tenant.shortName) }}</span>
                <nz-tag class="tenant-type-tag" [ngClass]="getTenantTypeClass(tenant.type)" [testId]="'tenant-item-type'">
                  {{ getTenantTypeText(tenant.type) }}
                </nz-tag>
              </div>
              <div class="tenant-line2" *ngIf="tenant.shortName && tenant.shortName !== tenant.name">{{ tenant.shortName }}</div>
            </div>
            <i
              *ngIf="tenant.id === currentTenant?.id"
              nz-icon
              nzType="check"
              class="ml-auto tenant-check"
              [testId]="'tenant-item-selected-check'"
            ></i>
          </div>
        </div>
      </div>
    </nz-dropdown-menu>
  `,
  standalone: true,
  imports: [CommonModule, NzDropDownModule, NzIconModule, NzAvatarModule, NzTagModule, I18nPipe, TestIdDirective],
  styles: [
    `
      .tenant-selector {
        cursor: pointer;
        transition: all 0.3s;
        padding: 4px 8px;
        border-radius: 4px;
      }
      .tenant-selector:hover {
        background-color: rgba(0, 0, 0, 0.05);
      }
      .tenant-avatar {
        border: 1px solid #f0f0f0;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
      }
      .tenant-name {
        font-weight: 500;
        max-width: 120px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .tenant-arrow {
        font-size: 12px;
        color: #8c8c8c;
        width: 10px;
        height: 14px;
      }
      .tenant-menu {
        width: 280px;
        max-height: 400px;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 4px 0;
        border-radius: 4px;
        box-shadow:
          0 3px 6px -4px rgba(0, 0, 0, 0.12),
          0 6px 16px 0 rgba(0, 0, 0, 0.08),
          0 9px 28px 8px rgba(0, 0, 0, 0.05);
      }
      .tenant-header {
        color: #8c8c8c;
        border-bottom: 1px solid #f0f0f0;
        padding: 8px 12px;
        margin-bottom: 4px;
        font-size: 13px;
      }
      .tenant-item {
        padding: 14px 16px;
        transition: all 0.3s;
      }
      .tenant-item:hover {
        background-color: #f5f5f5;
      }
      .tenant-item-selected {
        background-color: #e6f7ff;
        color: #1890ff;
      }
      .tenant-item-selected:hover {
        background-color: #e6f7ff;
      }
      .tenant-item-text {
        display: flex;
        flex-direction: column;
        min-width: 0;
      }
      .tenant-line1 {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: nowrap;
      }
      .tenant-item-name {
        font-size: 14px;
        font-weight: 500;
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .tenant-line2 {
        font-size: 12px;
        color: #8c8c8c;
        max-width: 220px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .tenant-type-tag {
        margin-left: 0;
        height: 18px;
        line-height: 16px;
        font-size: 11px;
        border: none;
      }
      .tenant-type-tag.type-official {
        color: #faad14;
        background: #fffbe6;
        border-color: #ffe58f;
      }
      .tenant-type-tag.type-person {
        color: #389e0d;
        background: #f6ffed;
        border-color: #b7eb8f;
      }
      .tenant-type-tag.type-team {
        color: #1890ff;
        background: #e6f7ff;
        border-color: #91d5ff;
      }
      .tenant-check {
        color: #1890ff;
        font-size: 14px;
      }
    `
  ]
})
export class HeaderTenantComponent implements OnInit {
  private readonly http = inject(HttpService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  private readonly imageService = inject(ImageService); // 修改为注入 ImageService
  private readonly startupService = inject(StartupService);
  private readonly message = inject(NzMessageService);
  private readonly router = inject(Router);
  private readonly titleService = inject(TitleService);
  private readonly tenantSwitchService = inject(TenantSwitchService);

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly globalGuardService = inject(GlobalGuardService);

  tenantList: any[] = [];
  currentTenant: any = null;

  // 默认头像
  private readonly defaultAvatar = './assets/images/tenant/default-tenant.png';
  // 缓存键前缀
  private readonly CACHE_KEY_PREFIX = 'tenant_avatar_';
  // 缓存过期时间（24*30 天，即 30 天，30 天后过期，每 30 天刷新一次，最多缓存 30 天的数据）
  private readonly CACHE_EXPIRY = 30 * 24 * 60 * 60 * 1000;

  ngOnInit(): void {
    // 初始化时立即加载租戶清單
    this.loadRelationList();
  }
  // 添加加载状态标志
  isLoading = false;
  // 上次加载时间戳
  lastLoadTime = 0;
  // 最小加载间隔（毫秒）
  readonly MIN_LOAD_INTERVAL = 5000; // 5秒

  loadRelationList() {
    // 检查是否正在加载或者距离上次加载时间太短
    const now = Date.now();
    if (this.isLoading || now - this.lastLoadTime < this.MIN_LOAD_INTERVAL) {
      return;
    }

    this.isLoading = true;
    this.lastLoadTime = now;

    this.http
      .post(ConstantUtil.TENANT_RELATION_LIST, {})
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.tenantList = res.data;
          this.processTenantData();
          this.cdr.detectChanges();
        }
      });
  }
  private processTenantData(): void {
    // 并行处理：预加载头像的同时处理租户信息
    const cachedTenant = this.getCachedTenantInfo();
    const foundTenant = this.findCachedTenant(cachedTenant);

    // 启动头像预加载（不等待结果）
    this.preloadTenantAvatars();

    if (this.shouldUseCachedTenant(foundTenant, cachedTenant)) {
      this.handleCachedTenant(foundTenant);
    } else {
      this.handleNewTenant();
    }
  }
  private findCachedTenant(cachedTenant: any): any {
    return cachedTenant ? this.tenantList.find(t => t.id === cachedTenant.id) : null;
  }

  getTenantTypeColor(type: string): string {
    const map: Record<string, string> = {
      Official: 'gold',
      Person: 'green',
      Common: 'blue',
      Public: 'blue',
      Service: 'blue'
    };
    return map[type] ?? 'blue';
  }

  getTenantTypeText(type: string): string {
    const map: Record<string, string> = {
      Official: 'tenant.detail.tenant-type.official',
      Person: 'tenant.detail.tenant-type.person',
      Common: 'tenant.detail.tenant-type.common',
      Public: 'tenant.detail.tenant-type.public',
      Service: 'tenant.detail.tenant-type.service'
    };
    return this.i18n.fanyi(map[type] ?? 'tenant.detail.tenant-type.common');
  }
  getTenantTypeClass(type: string): string {
    if (type === 'Official') return 'type-official';
    if (type === 'Person') return 'type-person';
    return 'type-team';
  }
  private shouldUseCachedTenant(foundTenant: any, cachedTenant: any): boolean {
    return foundTenant && foundTenant.id === cachedTenant?.id;
  }
  private handleCachedTenant(foundTenant: any): void {
    const cachedTenant = this.getCachedTenantInfo();
    const effectiveTenant = {
      ...(cachedTenant ?? {}),
      ...foundTenant,
      capabilities: cachedTenant?.capabilities ?? [],
      capabilityCodes: cachedTenant?.capabilityCodes ?? []
    };
    this.currentTenant = effectiveTenant;
    // 優先以包含既有能力之租戶資料重建選單，避免刷新時因 relation/list 無能力欄位而導致入口閃退
    this.tenantSwitchService.updateMenus(effectiveTenant);
    // 非同步向後端同步最新租戶能力，校準權威狀態
    this.tenantSwitchService.syncTenantCapabilities(effectiveTenant).subscribe({
      next: updated => {
        if (this.currentTenant?.id === updated?.id) {
          this.currentTenant = updated;
          this.cdr.detectChanges();
        }
      }
    });
  }

  // 处理新租户
  private handleNewTenant(): void {
    const lastTenant = this.tenantList.find(tenant => tenant.isLastTenant === true);
    this.currentTenant = lastTenant || this.getFirstAvailableTenant();

    if (this.currentTenant) {
      this.switchToNewTenant();
    }
  }
  private getFirstAvailableTenant(): any {
    return this.tenantList.length > 0 ? this.tenantList[0] : null;
  }

  // 切换到新租户
  private switchToNewTenant(): void {
    this.tenantSwitchService.switchTenant(this.currentTenant, this.tenantList).subscribe({
      next: res => {
        this.tenantSwitchService.navigateToDashboard(this.currentTenant);
      }
    });
  }

  // 获取缓存的租户信息
  private getCachedTenantInfo(): any {
    const cachedTenantStr = localStorage.getItem('current_tenant');
    if (!cachedTenantStr) return null;

    try {
      return JSON.parse(cachedTenantStr);
    } catch (e) {
      console.warn('解析缓存的租户信息失败:', e);
      return null;
    }
  }
  formatName(name: string): string {
    if (!name) return '';
    const arr = Array.from(name.trim());
    const max = 11;
    if (arr.length <= max) return name.trim();
    return `${arr.slice(0, max).join('')}...`;
  }
  // 预加载所有租户头像
  private preloadTenantAvatars(): void {
    // 收集需要加载的头像请求
    const avatarRequests: Array<Observable<any>> = [];

    this.tenantList.forEach(tenant => {
      if (tenant.avatarId && !this.getAvatarFromCache(tenant.avatarId)) {
        // 创建请求但不立即执行，使用 imageService
        const request = this.imageService.loadImage(tenant.avatarId, 'M').pipe(
          catchError(err => {
            console.error('获取租户头像失败:', err);
            // 设置一个空值，避免重复请求失败的头像
            this.saveAvatarToCache(tenant.avatarId, '');
            return of(null); // 返回空值但不中断其他请求
          })
        );

        avatarRequests.push(request);
      }
    });

    // 如果有需要加载的头像，使用forkJoin并行加载
    if (avatarRequests.length > 0) {
      forkJoin(avatarRequests).subscribe({
        next: results => {
          // 处理结果，将头像保存到缓存
          results.forEach((res, index) => {
            if (res) {
              // 找到对应的租户
              const tenant = this.tenantList.find(t => t.avatarId && !this.getAvatarFromCache(t.avatarId));
              if (tenant) {
                this.saveAvatarToCache(tenant.avatarId, res);
              }
            }
          });
          this.cdr.detectChanges();
        }
      });
    }
  }

  // 从缓存获取头像
  private getAvatarFromCache(avatarId: string): string | null {
    const cacheKey = this.CACHE_KEY_PREFIX + avatarId;
    const cachedData = localStorage.getItem(cacheKey);

    if (!cachedData) return null;

    try {
      const { url, timestamp } = JSON.parse(cachedData);
      // 检查缓存是否过期
      if (Date.now() - timestamp > this.CACHE_EXPIRY) {
        localStorage.removeItem(cacheKey);
        return null;
      }
      return url;
    } catch (e) {
      localStorage.removeItem(cacheKey);
      return null;
    }
  }

  // 将头像保存到缓存
  private saveAvatarToCache(avatarId: string, url: string): void {
    const cacheKey = this.CACHE_KEY_PREFIX + avatarId;
    const cacheData = {
      url,
      timestamp: Date.now()
    };
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  }
  // 获取租户头像，优先从缓存获取
  getTenantAvatar(tenant: any): string {
    if (!tenant) return this.defaultAvatar;

    // 如果有logoUrl，直接使用
    if (tenant.logoUrl) return tenant.logoUrl;

    // 如果有avatarId，从缓存获取
    if (tenant.avatarId) {
      const cachedAvatar = this.getAvatarFromCache(tenant.avatarId);

      // 如果缓存中有非loading状态的值，直接使用
      if (cachedAvatar && cachedAvatar !== 'loading') {
        return cachedAvatar !== '' ? cachedAvatar : this.defaultAvatar;
      }

      // 返回默认头像，头像会通过并行加载更新
      return this.defaultAvatar;
    }

    return this.defaultAvatar;
  }

  async switchTenant(tenant: any): Promise<void> {
    if (tenant.id === this.currentTenant?.id) {
      return;
    }

    // 使用 GlobalGuardService 检查是否可以离开当前页面
    const canLeave = await this.globalGuardService.checkCanDeactivate();

    if (canLeave) {
      // 清理當前頁面的未保存狀態
      const currentComponent = this.globalGuardService.getCurrentComponent();
      if (currentComponent && 'hasUnsavedChanges' in currentComponent) {
        (currentComponent as any).hasUnsavedChanges = false;

        if (typeof (currentComponent as any).resetChangeDetection === 'function') {
          (currentComponent as any).resetChangeDetection();
        }
      }

      this.performTenantSwitch(tenant);
    } else {
      console.log('租戶切換被取消');
    }
  }

  private performTenantSwitch(tenant: any): void {
    this.tenantSwitchService.switchTenant(tenant, this.tenantList).subscribe({
      next: res => {
        this.currentTenant = tenant;
        this.message.success(this.i18n.fanyi('tenant.switch-success', { name: tenant.name }));
        this.tenantSwitchService.navigateToDashboard(tenant);
      }
    });
  }
  // 处理租户选择器的点击事件
  onTenantSelectorClick(event: MouseEvent): void {
    // 重新加载租户关系列表
    this.loadRelationList();
  }
}
